import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, MessageCircle, Minus, Plus, Check, ChevronRight, Home as HomeIcon, ShieldCheck, Truck, Award } from 'lucide-react';
import { getProduct, getProducts, getSettings, createOrder } from '../lib/api';
import { formatPrice, buildWhatsAppMessage } from '../data/assets';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import ProductCard from '../components/ProductCard';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    setQty(1); setActiveImg(0);
    getProduct(id).then(p => {
      setProduct(p);
      getProducts({ category: p.category }).then(list => setRelated(list.filter(x => x.id !== p.id).slice(0, 4)));
    }).catch(() => navigate('/shop'));
    getSettings().then(setSettings).catch(() => {});
  }, [id, navigate]);

  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-gray-400">Loading...</div>;

  const orderOnWhatsApp = async () => {
    const items = [{ product_id: product.id, name: product.name, price: product.price, quantity: qty, image: product.images[0] || '' }];
    let order = null;
    try {
      order = await createOrder({ customer_name: 'Website Customer', phone: '', address: '', note: 'Direct WhatsApp order', items });
    } catch (e) { /* still proceed to WhatsApp */ }
    const msg = buildWhatsAppMessage(items, product.price * qty, order ? { order_number: order.order_number } : null);
    const num = (settings.whatsapp_number || '923001234567').replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
    toast({ title: 'Opening WhatsApp', description: 'Complete your order in the chat.' });
  };

  const handleAddToCart = () => {
    addToCart(product, qty);
    toast({ title: 'Added to cart', description: `${qty} x ${product.name}` });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-[#0f7a37] flex items-center gap-1"><HomeIcon size={14} /> Home</Link>
        <ChevronRight size={14} />
        <Link to={`/shop?category=${encodeURIComponent(product.category)}`} className="hover:text-[#0f7a37]">{product.category}</Link>
        <ChevronRight size={14} />
        <span className="text-[#14181b] font-medium line-clamp-1">{product.name}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* gallery */}
        <div>
          <div className="bg-[#f6faf7] rounded-2xl p-8 flex items-center justify-center aspect-square border border-gray-100">
            <img src={product.images[activeImg]} alt={product.name} className="max-h-full max-w-full object-contain" />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-xl border-2 p-2 bg-[#f6faf7] transition-colors ${activeImg === i ? 'border-[#0f7a37]' : 'border-gray-100 hover:border-gray-300'}`}>
                  <img src={im} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* info */}
        <div>
          <span className="text-sm font-semibold text-[#37b34a] uppercase tracking-wide">{product.category}</span>
          <h1 className="font-head text-2xl md:text-3xl font-extrabold text-[#14181b] mt-2 leading-tight">{product.name}</h1>
          <div className="flex items-center gap-3 mt-4">
            <span className="text-3xl font-extrabold text-[#0d5228]">{formatPrice(product.price)}</span>
            {product.in_stock ? (
              <span className="text-xs font-semibold text-[#0f7a37] bg-[#eaf6ec] px-3 py-1 rounded-full flex items-center gap-1"><Check size={13} /> In Stock</span>
            ) : (
              <span className="text-xs font-semibold text-red-500 bg-red-50 px-3 py-1 rounded-full">Out of Stock</span>
            )}
          </div>

          <p className="text-gray-600 mt-5 leading-relaxed">{product.short_description}</p>

          {/* qty + actions */}
          <div className="flex items-center gap-4 mt-7">
            <div className="flex items-center border-2 border-gray-200 rounded-full overflow-hidden">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="px-3.5 py-2.5 hover:bg-gray-100 transition-colors"><Minus size={16} /></button>
              <span className="px-5 font-semibold w-14 text-center">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="px-3.5 py-2.5 hover:bg-gray-100 transition-colors"><Plus size={16} /></button>
            </div>
            <button onClick={handleAddToCart}
              className="nl-btn flex-1 border-2 border-[#0f7a37] text-[#0f7a37] hover:bg-[#0f7a37] hover:text-white px-5 py-3">
              <ShoppingCart size={18} /> Add to Cart
            </button>
          </div>
          <button onClick={orderOnWhatsApp}
            className="nl-btn w-full bg-[#25D366] hover:bg-[#1eb457] text-white px-5 py-3.5 mt-3 text-base">
            <MessageCircle size={20} /> Order on WhatsApp
          </button>

          {/* trust */}
          <div className="grid grid-cols-3 gap-3 mt-7 pt-6 border-t border-gray-100">
            {[{ i: ShieldCheck, t: '14-Month Warranty' }, { i: Truck, t: 'Fast Delivery' }, { i: Award, t: 'Premium Quality' }].map((x, k) => (
              <div key={k} className="text-center">
                <x.i size={22} className="mx-auto text-[#0f7a37]" />
                <div className="text-xs text-gray-600 mt-1.5 font-medium">{x.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* description + features */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
        <div className="md:col-span-2">
          <h2 className="font-head text-xl font-bold text-[#14181b] mb-4 pb-2 border-b-2 border-[#37b34a] inline-block">Description</h2>
          <p className="text-gray-600 leading-relaxed whitespace-pre-line">{product.description}</p>
        </div>
        <div>
          <h2 className="font-head text-xl font-bold text-[#14181b] mb-4 pb-2 border-b-2 border-[#37b34a] inline-block">Key Features</h2>
          <ul className="space-y-2.5">
            {product.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                <Check size={17} className="text-[#0f7a37] shrink-0 mt-0.5" /> {f}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {related.length > 0 && (
        <div className="mt-16">
          <h2 className="font-head text-2xl font-extrabold text-[#14181b] mb-6">Related Products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
